import { useTranslation } from 'react-i18next'
import { Edit02Icon as EditIcon, Trash01Icon as TrashIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import {
  countUpcomingOverrides,
  getActiveOverride,
  mergeLegacyClosedDates,
  todayISO,
  type AvailabilitySchedule,
  type TransferOverride,
} from './transferOverrides'

export type { AvailabilitySchedule, TransferOverride }

export interface TransferRule {
  number: string
  reasons: string[]
  availability: AvailabilitySchedule[]
  // Optional: legacy rules saved before this field existed may lack it
  closed_dates?: string[]
  closed_instruction: string
  unavailable_instruction: string
  overrides?: TransferOverride[]
}

export interface TransferFlowCardProps {
  rule: TransferRule
  index: number
  onEdit: () => void
  onDelete: () => void
}

export function TransferFlowCard({ rule, onEdit, onDelete }: TransferFlowCardProps) {
  const { t, i18n } = useTranslation()
  const today = todayISO()
  // Include unmigrated legacy closed_dates so badges match what the modal shows
  const effectiveOverrides = mergeLegacyClosedDates(rule.overrides ?? [], rule.closed_dates)
  const activeOverride = getActiveOverride(effectiveOverrides, today)
  const upcomingCount = countUpcomingOverrides(effectiveOverrides, today)

  // Parse as local date parts: new Date('YYYY-MM-DD') is UTC midnight and can
  // render the previous day in timezones behind UTC
  const formatDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number)
    if (!year || !month || !day) return iso
    return new Date(year, month - 1, day).toLocaleDateString(i18n.language)
  }

  // Recurring periods repeat every year, so their year would be misleading
  const formatDayMonth = (iso: string) => {
    const [, month, day] = iso.split('-').map(Number)
    if (!month || !day) return iso
    return new Date(2000, month - 1, day).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
  }

  const endLabel = activeOverride
    ? activeOverride.recurring
      ? formatDayMonth(activeOverride.end_date)
      : formatDate(activeOverride.end_date)
    : ''
  const baseBadgeText = activeOverride
    ? activeOverride.mode === 'closed'
      ? t('resources.assistants.transfer_flows.override_active_closed', { date: endLabel })
      : t('resources.assistants.transfer_flows.override_active_custom', { date: endLabel })
    : null
  const activeBadgeText =
    baseBadgeText && activeOverride?.recurring
      ? `${baseBadgeText} · ${t('resources.assistants.transfer_flows.override_recurring_badge')}`
      : baseBadgeText

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {rule.number}
        </p>
        <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
          {rule.reasons?.join(', ') || t('resources.assistants.transfer_flows.no_reasons_specified')}
        </p>
        {(activeBadgeText || upcomingCount > 0) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {activeBadgeText && (
              <span className="inline-flex items-center rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-900/20 dark:text-warning-400">
                {activeOverride?.label ? `${activeOverride.label} · ${activeBadgeText}` : activeBadgeText}
              </span>
            )}
            {upcomingCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {t('resources.assistants.transfer_flows.override_planned', { count: upcomingCount })}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button color="tertiary" size="sm" onPress={onEdit} className="p-2" iconLeading={EditIcon} />
        <Button color="tertiary" size="sm" onPress={onDelete} className="p-2 text-error-600 hover:text-error-700" iconLeading={TrashIcon} />
      </div>
    </div>
  )
}

export default TransferFlowCard
